import Container from "@/components/Container/Container";
import Layout from "@/components/Layout/Layout";
import { H2Css, H5Css, Heading } from "@/components/Typography";
import { mq } from "@/constants/breakpoints";
import { contentfulClient } from "@/utils/contentful";
import { css } from "@emotion/react";
import { GetStaticProps } from "next";
import { useMemo, useState } from "react";
import faqIconImage from "../../public/images/pricing/faq-icon.svg";
import FAQItem from "@/components/FAQ/FAQItem";
// import TrustpilotCarousel from "@/components/Trustpilot/TrustpilotCarousel";
import BottomCTA from "@/components/BottomCTA/BottomCTA";
import FAQTitle from "@/components/FAQ/FAQTitle";
import PlanSection from "@/components/Pricing/PlanSection";
import FeatureTable from "@/components/Pricing/FeatureTable";
import { PromotionalBanner } from "@/components/Pricing/PromotionalBanner";
import ShortTestimonialsSlider from "@/components/Testimonial/ShortTestimonialsSlider";
import MobilePricingSummary from "@/components/Pricing/MobilePricingSummary";

const Page = ({ fields }: any) => {
  const [isAnnual, setIsAnnual] = useState(true);

  const { firstHalfFAQs, secondHalfFAQs } = useMemo(() => {
    const firstHalfFAQs = fields.faqItems.slice(
      0,
      Math.ceil(fields.faqItems.length / 2)
    );
    const secondHalfFAQs = fields.faqItems.slice(
      Math.ceil(fields.faqItems.length / 2),
      fields.faqItems.length
    );
    return { firstHalfFAQs, secondHalfFAQs };
  }, [fields.faqItems]);

  return (
    <Layout
      title={fields.pageTitle}
      metaDescription={fields.metaDescription}
      heroContainerCss={css`
        background: url(/images/pricing/BG.svg),
          linear-gradient(112.24deg, #048cdd 4.51%, #32b2ff 100.81%);
        background-size: cover;
        background-repeat: no-repeat;
        min-height: fit-content;
      `}
      hero={
        <Container>
          <Heading
            level={2}
            css={css`
              color: #fff;
              text-align: center;
              max-width: 969px;
              padding: 10px;
              padding-top: 24px;
              padding-bottom: 50px;

              ${H5Css}
              ${mq["xs"]} {
                font-size: 26px;
              }
              ${mq["sm"]} {
                font-size: 28px;
              }
              ${mq["lg"]} {
                padding-top: 64px;
                padding-bottom: 80px;
                margin-left: auto;
                margin-right: auto;
                ${H2Css}
              }
            `}
          >
            {fields.heroHeading ||
              "Everything You Need to Run Your Sports Organization — At a Price That Fits Your Budget"}
          </Heading>
        </Container>
      }
    >
      <PromotionalBanner specialOffer={fields.promotionalBanner?.fields} />
      <Container
        css={css`
          margin-top: 16px;

          ${mq["lg"]} {
            margin-top: 100px;
            padding-left: 0px;
            padding-right: 0px;
          }
        `}
      >
        <PlanSection
          isAnnual={isAnnual}
          setIsAnnual={setIsAnnual}
          pricingPlans={fields.pricingPlans?.map((plan: any) => plan.fields)}
        />
      </Container>

      <Container>
        <MobilePricingSummary
          isAnnual={isAnnual}
          pricingPlans={fields.pricingPlans?.map((plan: any) => plan.fields)}
        />
      </Container>

      <Container
        css={css`
          ${mq["lg"]} {
            margin-top: 32px;
          }
        `}
      >
        <FeatureTable featureTableData={fields.featureTable.fields} />
      </Container>

      <Container>
        <div
          css={css`
            margin-top: 60px;
            ${mq["lg"]} {
              margin-top: 120px;
            }
            position: relative;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-grow: 1;
              flex-direction: column;
              position: relative;
            `}
          >
            <div
              css={css`
                margin-right: calc(-1 * (100vw - 100%) / 2);
              `}
            >
              <ShortTestimonialsSlider
                testimonials={fields?.testimonials?.map((t: any) => t.fields)}
              />
            </div>
          </div>
        </div>
      </Container>

      <Container
        css={css`
          margin-top: 60px;
          ${mq["lg"]} {
            margin-top: 120px;
          }
        `}
      >
        <FAQTitle icon={faqIconImage} title="Frequently Asked Questions" />
        <div
          css={css`
            display: flex;
            width: 100%;
            flex-direction: column;
            ${mq["lg"]} {
              margin-top: 40px;
            }
            ${mq["xl"]} {
              flex-direction: row;
              gap: 105px;
            }
          `}
        >
          <div
            css={css`
              flex: 1;
            `}
          >
            {firstHalfFAQs.map((item: any, index: number) => (
              <FAQItem
                key={index}
                question={item.fields.question}
                answer={item.fields.answer}
              />
            ))}
          </div>
          <div
            css={css`
              flex: 1;
            `}
          >
            {secondHalfFAQs.map((item: any, index: number) => (
              <FAQItem
                key={index}
                question={item.fields.question}
                answer={item.fields.answer}
              />
            ))}
          </div>
        </div>
      </Container>
      {/* <Container
        css={css`
          padding: 60px 0;
          ${mq["lg"]} {
            padding: 120px 0;
          }
        `}
      >
        <Heading
          level={3}
          css={css`
            color: ${darkColor};
            text-align: center;
            ${H5Css}
            ${mq["lg"]} {
              ${H3Css}
            }
          `}
        >
          What customers are saying
        </Heading>
        <div css={css``}>
          <TrustpilotCarousel />
        </div>
      </Container> */}
      <BottomCTA />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const entries = await contentfulClient.getEntries({
    content_type: "pricingPage",
    include: 4,
  });

  const pricingPageEntry = entries.items.find(
    (item: any) => item.fields.type === "v7"
  );

  if (!pricingPageEntry) {
    return {
      notFound: true,
    };
  }

  const fields = pricingPageEntry.fields;

  return {
    props: { fields },
  };
};

export default Page;
